<template>
  <v-card
    ref="minimapContainer"
    :style="minimapStyle"
    elevation="6"
    class="guide-minimap-container"
    :class="{ 'minimap-embedded': embedded }"
  >
    <div
      v-show="!minimized"
      ref="minimap"
      class="minimap"
      :class="{ 'minimap-embedded-content': embedded }"
      data-testid="miniMap.miniMap-div"
    ></div>
    <v-toolbar id="minimapbarId" ref="minimapbar" flat density="compact" class="minimap-toolbar">
      <v-icon v-if="!embedded" ref="dragicon" color="font" data-testid="miniMap.drag-icon">mdi-drag</v-icon>
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn-icon icon="mdi-magnify-plus" v-bind="props" data-testid="miniMap.zoom-in-button" @click="zoomIn()">
          </v-btn-icon>
        </template>
        <span>Zoom In</span>
      </v-tooltip>

      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn-icon icon="mdi-magnify-minus" v-bind="props" data-testid="miniMap.zoom-out-button" @click="zoomOut()">
          </v-btn-icon>
        </template>
        <span>Zoom Out</span>
      </v-tooltip>

      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn-icon icon="mdi-arrow-expand-all" v-bind="props" @click="zoomFit()"> </v-btn-icon>
        </template>
        <span>Zoom To Fit</span>
      </v-tooltip>
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn-icon
            icon="mdi-arrow-collapse-all"
            v-bind="props"
            data-testid="miniMap.zoom-to-original-button"
            @click="zoomToOriginal()"
          >
          </v-btn-icon>
        </template>
        <span>Zoom To Original</span>
      </v-tooltip>

      <v-text-field
        v-show="showZoomField"
        v-model="zoomFactor"
        hide-details
        variant="solo"
        density="compact"
        type="number"
        suffix="%"
        data-testid="miniMap.zoom-factor-input"
        @keydown.enter="updateZoom()"
        @blur="updateZoom()"
        @click="updateZoom()"
      />
      <v-spacer></v-spacer>

      <v-tooltip v-if="!embedded" location="top">
        <template #activator="{ props }">
          <v-btn
            size="small"
            icon="mdi-unfold-more-horizontal"
            :color="minimized ? 'font' : 'primary'"
            v-bind="props"
            data-testid="miniMap.hide-show-button"
            @click="minMax()"
          >
          </v-btn>
        </template>
        <span>Show/Hide Map</span>
      </v-tooltip>
    </v-toolbar>
  </v-card>
</template>

<script setup lang="ts">
import { ui } from "@clientio/rappid";
import { Renderer } from "./services/renderer";
import DragHandler from "./services/drag-handler";
import RappidContext from "./rappid/rappid-context";
import { computed, inject, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { CardPosition, foldDirection } from "./utils/resizeCard";

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const dragicon = ref(null);

const minimapContainer = ref(null);
const minimap = ref<HTMLElement | null>(null);

const mapContentHeight = "200px";
const mapToolbarHeight = "48px";
const mapWidth = "370px";
const mapMaxHeight = parseInt(mapContentHeight) + parseInt(mapToolbarHeight) + "px";
const minWidth = 300;
const minHeight = 200;
const zoomFieldThreshold = 340;

const compProps = withDefaults(
  defineProps<{
    right?: string;
    transition?: string;
    embedded?: boolean;
  }>(),
  { right: "80px", transition: "none", embedded: false }
);

const position = reactive<CardPosition>({
  xLeft: "unset",
  xRight: compProps.right,
  yTop: "unset",
  yBottom: "20px",
});

watch(
  () => compProps.right,
  (value: string) => {
    position.xLeft = "unset";
    position.xRight = value;
    position.yTop = "unset";
    position.yBottom = "20px";
  }
);

const minimapStyle = computed<string>(() => {
  if (compProps.embedded) {
    return "width: 100%; height: 100%; position: relative; box-shadow: none;";
  }
  if (minimized.value) {
    return `
      height: ${mapToolbarHeight};
      width: ${currentCardWidth.value}px;
      min-width: ${minWidth}px;
      overflow: hidden;
      transition: ${compProps.transition};
      left: ${position.xLeft};
      right: ${position.xRight};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  } else {
    return `
      height: ${expandedCardHeight.value}px;
      width: ${currentCardWidth.value}px;
      min-width: ${minWidth}px;
      min-height: ${minHeight}px;
      overflow: hidden;
      resize: both;
      transition: ${compProps.transition};
      left: ${position.xLeft};
      right: ${position.xRight};
      top: ${position.yTop};
      bottom: ${position.yBottom};
      `;
  }
});

const nav = ref<ui.Navigator>();
const zoomOptions: ui.PaperScroller.ZoomOptions = { max: 4, min: 0.5 };
const zoomStep = 0.1;
const zoomFactor = ref(100);
const minimized = ref(true);
const currentCardWidth = ref(parseInt(mapWidth));
const expandedCardHeight = ref(parseInt(mapMaxHeight));
const showZoomField = computed(() => currentCardWidth.value >= zoomFieldThreshold);
let resizeObserver: ResizeObserver | null = null;

const { lgAndDown, height, mdAndDown } = useDisplay();

const dragContent = ref<HTMLElement>();

watch(mdAndDown, (newVal: boolean) => {
  if (!compProps.embedded && newVal && !minimized.value) {
    minimize();
  }
});

onMounted(() => {
  minimized.value = compProps.embedded ? false : lgAndDown.value;
  nav.value = new ui.Navigator({
    paperScroller: rappidContext.paperScroller,
    width: 0,
    height: 0,
    padding: 8,
    zoomOptions: zoomOptions,
  });
  rappidContext.paperScroller.on("scroll", () => updateZoomFactor());

  Renderer.renderView(".minimap", nav.value);

  if (!compProps.embedded) {
    const dragTarget = dragicon.value.$el as HTMLElement;
    dragContent.value = minimapContainer.value.$el as HTMLElement;
    new DragHandler(dragTarget, dragContent.value, position).enableDragging();
  }

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const w = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      currentCardWidth.value = Math.round(w);
      if (!minimized.value) {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        expandedCardHeight.value = Math.round(h);
      }
      syncNavigatorSize();
    }
  });
  resizeObserver.observe(minimapContainer.value.$el as HTMLElement);
  syncNavigatorSize();
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});

function updateZoomFactor() {
  zoomFactor.value = Math.round(rappidContext.paperScroller.zoom() * 100);
}

function syncNavigatorSize() {
  if (!nav.value || !minimap.value) {
    return;
  }

  const width = Math.round(minimap.value.clientWidth);
  const height = Math.round(minimap.value.clientHeight);

  if (width <= 0 || height <= 0) {
    return;
  }

  nav.value.options.width = width;
  nav.value.options.height = height;
  nav.value.el.style.width = `${width}px`;
  nav.value.el.style.height = `${height}px`;
  nav.value.updatePaper();
  nav.value.updateCurrentView();
}

function updateZoom() {
  const updateStep = (zoomFactor.value - Math.round(rappidContext.paperScroller.zoom() * 100)) / 100;
  rappidContext.paperScroller.zoom(updateStep, zoomOptions);

  zoomFactor.value = Math.max(Math.min(zoomFactor.value, zoomOptions.max * 100), zoomOptions.min * 100);
}

function zoomIn() {
  rappidContext.paperScroller.zoom(zoomStep, zoomOptions);
}

function zoomOut() {
  rappidContext.paperScroller.zoom(-zoomStep, zoomOptions);
}

function zoomFit() {
  rappidContext.paperScroller.zoomToFit({
    padding: { left: 300, top: 70, right: 300, bottom: 70 },
  });
}

function zoomToOriginal() {
  rappidContext.paperScroller.zoom(1 - rappidContext.paperScroller.zoom(), zoomOptions);
  rappidContext.paperScroller.centerContent();
}

function minMax() {
  minimized.value ? maximize() : minimize();
}

function getExpandedContentOffset() {
  return expandedCardHeight.value - parseInt(mapToolbarHeight);
}

function minimize() {
  if (foldDirection(dragContent, "min", height.value) === "upwards") {
    position.yTop = dragContent.value.getBoundingClientRect().top + "px";
  } else {
    position.yTop = dragContent.value.getBoundingClientRect().bottom - parseInt(mapToolbarHeight) + "px";
  }
  minimized.value = true;
  nav.value.freeze();
}

function maximize() {
  const expandedContentOffset = getExpandedContentOffset();

  if (foldDirection(dragContent, "max", height.value) === "upwards") {
    position.yTop = dragContent.value.getBoundingClientRect().top - expandedContentOffset + "px";
  } else {
    position.yTop = dragContent.value.getBoundingClientRect().top - expandedContentOffset + "px";
  }
  minimized.value = false;
  nav.value.unfreeze();
  nav.value.updateCurrentView();
}

function activate() {
  nav.value?.updateCurrentView();
}

function freezeNavListeners() {
  nav.value?.freeze();
}

function unfreezeNavListeners() {
  nav.value?.unfreeze();
  nav.value?.updateCurrentView();
}

defineExpose({ activate, freezeNavListeners, unfreezeNavListeners });
</script>
<style>
#minimapbarId .v-text-field--suffixed.v-text-field .v-field__input {
  padding-right: 5px;
  min-width: 70px;
}

#minimapbarId .v-field__field {
  align-items: center;
  height: 33px;
}
</style>
<style scoped>
.minimap {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: calc(100% - v-bind(mapToolbarHeight));
}
.minimap :deep(.navigator) {
  width: 100%;
  height: 100%;
}
.minimap-toolbar {
  position: absolute;
  bottom: 0px;
  width: 100%;
  border-top: 2px solid rgba(var(--v-theme-contrast-grey-medium));
  pointer-events: none;
}
.minimap-toolbar * {
  pointer-events: auto;
}
.minimap-embedded {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 200px;
}
.minimap-embedded-content {
  position: relative !important;
  width: 100% !important;
}
.minimap-embedded .minimap-toolbar {
  width: 100%;
}
</style>
